'use client'

import { useState, useEffect } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { createClient } from '@/lib/supabase/client'
import { buscarMeuPerfil } from '@/lib/perfil/meu-perfil'
import TransparentCheckout from '@/components/ui/transparent-checkout'

/* ============================================================================
   /mapa-de-contexto — oferta de entrada (Mapa de Contexto / base de comunicacao).
   Saiu da home quando a home virou a pagina do projeto Manifesto.
   Mantem o mesmo canal de venda ("home") pra nao mudar produto nem preco,
   e continua atendendo os links legados /?comprar=passaporte, que a home
   redireciona pra ca.

   O nome da pasta NAO pode ser 'passaporte': app/passaporte colide com
   app/app/passaporte no bundle (mesmo module id) e a variante protegida vence,
   devolvendo 307 pra /app/jornada. Ver brain: colisao-modulo-agenda-membros.
   ============================================================================ */

const INCLUI = [
  'A sua frase de mercado em uma linha',
  'O retrato do seu cliente ideal',
  'As palavras que viram a sua marca',
  'O mapa do que dá pra vender',
  'Legendas e a abertura da sua página, prontas',
  'Tudo salvo na sua biblioteca',
]

export default function PassaportePage() {
  const [showCheckout, setShowCheckout] = useState(false)
  const [checkoutError, setCheckoutError] = useState('')
  const [buyerName, setBuyerName] = useState<string | undefined>()
  const [buyerEmail, setBuyerEmail] = useState<string | undefined>()
  const [buyerPhone, setBuyerPhone] = useState<string | undefined>()
  const [buyerCpf, setBuyerCpf] = useState<string | undefined>()
  const router = useRouter()

  // ?comprar=1 abre o checkout sem clique extra. Quem chega assim veio de um CTA
  // que ja dizia "comprar" (pagina estatica /maquina-livros, deep-link legado da
  // home): pedir mais um clique so perde gente.
  useEffect(() => {
    const c = new URLSearchParams(window.location.search).get('comprar')
    if (c && c !== '0') setShowCheckout(true)
  }, [])

  useEffect(() => {
    const supabase = createClient()
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user) {
        setBuyerEmail(user.email || undefined)
        setBuyerName(user.user_metadata?.full_name || undefined)
        buscarMeuPerfil()
          .then(({ data: profile }) => {
            if (profile) {
              if (profile.full_name) setBuyerName(profile.full_name)
              if (profile.phone) setBuyerPhone(profile.phone)
              if (profile.document) setBuyerCpf(profile.document)
            }
          })
      }
    })
  }, [])

  return (
    <div className="min-h-screen bg-ink-9 px-4 py-12 sm:py-16">
      <div className="max-w-lg mx-auto">
        <div className="text-center mb-8">
          <p className="text-xs font-semibold text-brand-500 uppercase tracking-wider mb-3">Ponto de partida</p>
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-ink-0 tracking-tight">
            A sua base, montada numa tarde
          </h1>
          <p className="mt-3 text-sm sm:text-base text-ink-3 leading-relaxed">
            Uma entrevista guiada que devolve, por escrito, quem você atende, qual problema resolve e com quais palavras.
            É de onde toda a sua comunicação passa a nascer.
          </p>
        </div>

        <div className="bg-paper border border-ink-7 rounded-2xl p-6 sm:p-8 shadow-soft">
          <div className="text-center pb-6 border-b border-ink-7">
            <p className="text-sm text-ink-4 line-through">Preço justo: R$ 297</p>
            <p className="text-4xl sm:text-5xl font-black text-ink-0 mt-1">R$ 19,90</p>
            <p className="text-sm text-ink-4 mt-1">Pagamento único. Acesso imediato.</p>
          </div>

          <ul className="space-y-2.5 py-6">
            {INCLUI.map((item) => (
              <li key={item} className="flex items-start gap-2.5 text-sm text-ink-1">
                <svg className="w-4 h-4 text-brand-500 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                </svg>
                {item}
              </li>
            ))}
          </ul>

          {!showCheckout ? (
            <button
              onClick={() => {
                setShowCheckout(true)
                setCheckoutError('')
              }}
              className="w-full bg-brand-grad text-white py-4 rounded-full text-base font-bold shadow-glow hover:-translate-y-px transition cursor-pointer"
            >
              Começar agora
            </button>
          ) : (
            <div>
              {checkoutError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{checkoutError}</div>
              )}
              <TransparentCheckout
                channelSlug="home"
                buyerName={buyerName}
                buyerEmail={buyerEmail}
                buyerPhone={buyerPhone}
                buyerCpf={buyerCpf}
                onSuccess={(paymentId) => {
                  router.push('/obrigado?payment=' + paymentId)
                }}
                onError={(error) => setCheckoutError(error)}
              />
            </div>
          )}
        </div>

        <div className="mt-8 text-center space-y-3">
          <p className="text-sm text-ink-3">
            Procurando o projeto Manifesto?{' '}
            <Link href="/" className="font-semibold text-brand-500 hover:underline">
              Conheça aqui
            </Link>
            .
          </p>
          <p className="text-xs text-ink-4">
            Já tem conta?{' '}
            <Link href="/login" className="text-ink-2 hover:underline">
              Entrar
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}
